import axios from 'axios';
import { TOKEN_EXPIRED } from '../hooks/useAPIHelper';

/**
 * The counts behind Home's planned-maintenance cards, from the database (PORTAL-72), or null to mean
 * "count the list in the browser as before".
 *
 * WHY IT RETURNS NULL RATHER THAN THROWING. Counting the list in the browser is the path that has always
 * worked. Anything this call cannot do - the site not being set up for it, a slow or failed answer, or no
 * entitlement found for the person - is answered by returning null, and the caller counts as it did. The one
 * exception is an expired Pronto token, which every other call would also report, so it is thrown in the same
 * words and the caller's existing handling applies.
 *
 * NO ENTITLEMENT IS NOT ZERO. `entitlementRows` of 0 means the database found no contract list for the person,
 * which is a different statement from "they have no calls". Showing zeros there would look like an answer, so it
 * is returned as null and the browser counts instead.
 */

const SUMMARY_URL = '/.netlify/functions/xServiceGetPMCallSummary';

export type SummaryPeriod = 'today' | 'thisWeek' | 'thisMonth' | 'last3Months' | 'last6Months' | 'last12Months' | 'all';

export interface StatusCounts {
  open: number;
  completed: number;
  incomplete: number;
}

export interface PMCallSummary {
  // When the data was last copied from the Extract (ISO, UTC); null where unknown.
  dataAsOfUtc: string | null;
  counts: Record<SummaryPeriod, StatusCounts>;
}

const PERIODS: SummaryPeriod[] = ['today', 'thisWeek', 'thisMonth', 'last3Months', 'last6Months', 'last12Months', 'all'];

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;

const GetPMCallSummary = async (token: string, userName: string, customerId: string): Promise<PMCallSummary | null> => {
  const started = Date.now();

  try {
    const reply = await axios.post(
      SUMMARY_URL,
      `<Filters><CustomerCode><Like>${customerId}</Like></CustomerCode></Filters>`,
      { headers: { 'Content-Type': 'application/xml', 'X-Pronto-Token': token, 'X-Pronto-Username': userName } },
    );

    const data = reply.data;

    if (!(Number(data?.entitlementRows) > 0)) {
      console.log('PM call summary: no entitlement found for this person - counting in the browser');
      return null;
    }

    // Every period and status must be a real number. A partial answer shown as the full one would put a wrong
    // count on a card with nothing to say so.
    for (const period of PERIODS) {
      const cell = data?.counts?.[period];

      if (!cell || !isCount(cell.open) || !isCount(cell.completed) || !isCount(cell.incomplete)) {
        throw new Error(`the answer has no usable counts for '${period}'`);
      }
    }

    console.log(`PM call summary: counts in ${Date.now() - started} ms`);

    return { dataAsOfUtc: data.dataAsOfUtc ?? null, counts: data.counts as Record<SummaryPeriod, StatusCounts> };
  } catch (err: any) {
    if (err?.response?.status === 401 && err.response.data === 'TokenExpired') {
      throw TOKEN_EXPIRED;
    }

    console.log('PM call summary unavailable - counting in the browser:', err?.message ?? err);
    return null;
  }
};

export default GetPMCallSummary;
